/**
 * Insights → one company: every published story with a live link to share
 * (find-and-share, 0.3.6). Reached from the search box or any company name on
 * /insights; the URL is shareable.
 */
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { getCompanyArticles } from '../lib/api/insights.js';
import { InsightsSearch } from '../components/insights/InsightsSearch.js';
import { CopyAllButton, MASTHEAD_LABEL, StoryList } from '../components/insights/StoryList.js';
import { CompetitorTags, FirmLinks, RecencyLegend } from '../components/insights/Recency.js';
import { CompanyProfile, ReadershipReportButton } from '../components/insights/CompanyProfile.js';

const WINDOWS = [{ v: 30, l: 'Last 30 days' }, { v: 90, l: 'Last 90 days' }, { v: 365, l: 'Last year' }, { v: 0, l: 'All time' }];

function fmtCap(n: number | null): string | null {
  if (n === null) return null;
  return n >= 1e9 ? `$${(n / 1e9).toFixed(1)}B` : `$${Math.round(n / 1e6).toLocaleString()}M`;
}

export function InsightsCompanyPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const windowDays = Number(params.get('window') ?? 90);
  const q = useQuery({ queryKey: ['insights', 'company', id, windowDays], queryFn: () => getCompanyArticles(id, windowDays) });
  const d = q.data;
  const cap = d ? fmtCap(d.market_cap) : null;

  return (
    <div className="space-y-4 max-w-5xl mx-auto px-4 py-6">
      <Helmet><title>{d ? `${d.ticker ?? ''} · Insights` : 'Insights'}</title></Helmet>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/insights" className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-grey-800"><ArrowLeft className="h-4 w-4" /> Insights</Link>
        <InsightsSearch />
      </div>

      {q.isLoading && <p className="text-sm text-grey-400 py-10 text-center">Loading…</p>}
      {q.error && <p className="text-sm text-red-600 py-10 text-center">Could not load this company.</p>}
      {d && (
        <>
          <header className="bg-surface rounded-xl border border-grey-200 p-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-grey-900">{d.ticker} <span className="font-normal text-grey-500">· {d.name}</span></h1>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-grey-600">
                {cap && <span>Market cap {cap}</span>}
                {d.hub && (
                  <a href={d.hub.url} target="_blank" rel="noopener noreferrer"
                    title={d.hub.owner ? 'The company\'s hub on its owner masthead' : 'The owner masthead has no hub for this company yet'}
                    className="inline-flex items-center gap-1 text-primary-700 hover:underline">
                    Company hub · {MASTHEAD_LABEL[d.hub.masthead] ?? d.hub.masthead}<ExternalLink className="h-3 w-3" />
                  </a>
                )}
                <span>IR/PR: <FirmLinks links={d.firms} empty="none observed" /></span>
                {d.pipeline.is_customer && <span className="text-primary-700">Customer</span>}
                {d.pipeline.is_prospect && <span className="text-primary-700">Prospect</span>}
                {d.pipeline.campaigns.map((c) => <span key={c} className="text-primary-700">{c}</span>)}
              </div>
              {d.competitors.length > 0 && <div className="mt-1.5"><CompetitorTags items={d.competitors} /></div>}
            </div>
            <div className="flex items-center gap-2">
              <select aria-label="Window" value={windowDays}
                onChange={(e) => { const p = new URLSearchParams(params); p.set('window', e.target.value); setParams(p, { replace: true }); }}
                className="rounded-lg border border-grey-200 bg-surface px-2 py-1 text-xs text-grey-700">
                {WINDOWS.map((w) => <option key={w.v} value={w.v}>{w.l}</option>)}
              </select>
              <CopyAllButton heading={`Recent coverage of ${d.name ?? d.ticker}:`} stories={d.stories} />
              <ReadershipReportButton d={d} windowLabel={WINDOWS.find((w) => w.v === windowDays)?.l ?? 'Selected period'} />
            </div>
          </header>
          <CompanyProfile d={d} />
          <RecencyLegend />
          <section className="bg-surface rounded-xl border border-grey-200 px-4">
            <div className="pt-3 text-xs text-grey-500">{d.stories.length} {d.stories.length === 1 ? 'story' : 'stories'}</div>
            <StoryList stories={d.stories} onCompany={(eid) => navigate(`/insights/company/${eid}`)} />
          </section>
        </>
      )}
    </div>
  );
}
